import { useEffect, useState } from "react";
import { PlugZap, Sun, X, Play, Square, Clock, PauseCircle, Info, ChevronDown, Plus, TrendingDown, Car, BatteryCharging, Search, Fuel, Gauge } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, Cell, ReferenceLine, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { COL, pw, kwh } from "./clienteApi";

const hhmm = (ts) => { try { return new Date(ts).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }); } catch { return ""; } };
const fdt = (ts) => { try { return new Date(ts).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); } catch { return "—"; } };
const durata = (s) => { if (s == null) return ""; const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); return h ? `${h}h ${m}m` : `${m}m`; };

function Idx({ label, value, accent }) {
  return (
    <div className="rounded-xl bg-white p-2 ring-1 ring-slate-100" data-testid="cliente-ev-idx">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="text-base font-extrabold text-slate-800" style={accent ? { color: accent } : undefined}>{value}</div>
    </div>
  );
}

function Toggle({ on, onChange, disabled, testid }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} disabled={disabled} onClick={onChange} data-testid={testid}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${on ? "bg-sky-600" : "bg-slate-300"}`}>
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${on ? "translate-x-[22px]" : "translate-x-0.5"}`} />
    </button>
  );
}

const kwTxt = (w) => (w == null ? "—" : `${(w / 1000).toFixed(1)} kW`);
const eur = (v) => (v == null ? "—" : `${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);

const LOG_ICON = {
  sole: { I: Sun, c: "text-amber-500" },
  pausa: { I: PauseCircle, c: "text-slate-400" },
  play: { I: Play, c: "text-emerald-600" },
  stop: { I: Square, c: "text-rose-500" },
  info: { I: Info, c: "text-sky-600" },
};

// Mini-grafico nel box dinamica: confronta nel tempo il "tetto dal sole" (limite applicato)
// con la "potenza erogata" davvero all'auto, per vedere quanto bene insegue il sole.
function MiniSerie({ src, sid, tick }) {
  const [pts, setPts] = useState(null);
  useEffect(() => {
    if (!sid) { setPts([]); return; }
    let vivo = true;
    src.evMeter(sid).then((r) => {
      if (!vivo) return;
      const raw = r?.points || [];
      let lastCap = null;
      const mapped = raw.map((p) => {
        if (p.limit_w != null) lastCap = p.limit_w;
        return {
          t: hhmm(p.ts),
          tetto: lastCap != null ? +(lastCap / 1000).toFixed(2) : null,
          erogata: p.power_w != null ? +(p.power_w / 1000).toFixed(2) : null,
        };
      });
      const step = Math.max(1, Math.ceil(mapped.length / 48));
      setPts(mapped.filter((_, i) => i % step === 0 || i === mapped.length - 1));
    }).catch(() => vivo && setPts([]));
    return () => { vivo = false; };
  }, [sid, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!pts || pts.length < 2) return null;
  return (
    <div data-testid="cliente-ev-dinamica-serie">
      <div className="mb-1 flex items-center justify-between text-[10px] font-semibold">
        <span className="uppercase tracking-wide text-slate-400">Sole vs erogata nel tempo</span>
        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-amber-600"><span className="h-2 w-2 rounded-full bg-amber-400" />tetto dal sole</span>
          <span className="flex items-center gap-1 text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500" />erogata</span>
        </span>
      </div>
      <div className="h-28 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={pts} margin={{ top: 4, right: 6, left: -18, bottom: 0 }}>
            <defs><linearGradient id="erogGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient></defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1e9d6" />
            <XAxis dataKey="t" tick={{ fontSize: 9 }} minTickGap={30} />
            <YAxis tick={{ fontSize: 9 }} width={28} unit="" />
            <Tooltip formatter={(v, n) => [`${v} kW`, n === "tetto" ? "Tetto dal sole" : "Erogata"]} />
            <Area type="stepAfter" dataKey="tetto" stroke="#f59e0b" strokeWidth={1.5} strokeDasharray="4 3" fill="none" connectNulls isAnimationActive={false} />
            <Area type="monotone" dataKey="erogata" stroke="#10b981" strokeWidth={2} fill="url(#erogGrad)" connectNulls isAnimationActive={false} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// Casella che si apre sotto il toggle quando la carica dinamica è attiva:
// mostra la modalità (stima vs surplus reale) e lo storico delle azioni ragionate da Vesta.
function DinamicaLog({ src, st, tick }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let vivo = true;
    src.evDinamicaLog(st.id).then((r) => vivo && setD(r)).catch(() => vivo && setD({ errore: true }));
    return () => { vivo = false; };
  }, [st.id, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) return <div className="mt-2 rounded-xl bg-amber-50/60 p-3 text-xs text-slate-400" data-testid="cliente-ev-dinamica-log-loading">Carico lo storico…</div>;
  if (d.errore) return null;
  const stima = d.modalita === "stima_produzione";
  return (
    <div className="mt-2 space-y-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="cliente-ev-dinamica-log">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Cosa sta facendo Vesta</span>
        <span data-testid="cliente-ev-dinamica-modalita"
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${stima ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>
          {stima ? "Stima da sola produzione" : "Surplus reale attivo"}
        </span>
      </div>
      <p className="text-[11px] leading-relaxed text-slate-500" data-testid="cliente-ev-dinamica-nota">{d.nota}</p>
      {(d.max_sole_w != null || d.erogata_w != null) && (
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-lg bg-white p-2 ring-1 ring-amber-100" data-testid="cliente-ev-dinamica-max-sole">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Massimo dal sole</div>
            <div className="text-base font-extrabold text-amber-600">{kwTxt(d.max_sole_w)}</div>
          </div>
          <div className="rounded-lg bg-white p-2 ring-1 ring-amber-100" data-testid="cliente-ev-dinamica-erogata">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Sto erogando adesso</div>
            <div className="text-base font-extrabold text-emerald-600">{kwTxt(d.erogata_w)}</div>
          </div>
        </div>
      )}
      <MiniSerie src={src} sid={st.session_id} tick={tick} />
      {d.azione_corrente && <p className="text-xs font-semibold text-slate-700">Adesso: {d.azione_corrente}</p>}
      <div className="max-h-44 space-y-1.5 overflow-y-auto" data-testid="cliente-ev-dinamica-storico">
        {d.storico.length === 0 ? (
          <p className="py-2 text-center text-xs text-slate-400">Ancora nessuna azione registrata. Appena il sole varia, qui vedrai cosa decide Vesta.</p>
        ) : d.storico.map((r, i) => {
          const ic = LOG_ICON[r.icona] || LOG_ICON.info;
          return (
            <div key={i} className="flex items-start gap-2 rounded-lg bg-white p-2 ring-1 ring-amber-100" data-testid="cliente-ev-dinamica-riga">
              <ic.I className={`mt-0.5 h-4 w-4 shrink-0 ${ic.c}`} />
              <div className="min-w-0">
                <div className="text-xs leading-snug text-slate-700">{r.testo}</div>
                <div className="text-[10px] text-slate-400">{fdt(r.at)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Casella che si apre sotto il toggle quando l'ottimizzazione a PUN è attiva:
// mostra il criterio adottato, l'azione in corso e lo storico delle decisioni di Vesta sui prezzi PUN.
function PunLog({ src, st, tick }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let vivo = true;
    src.evPunLog(st.id).then((r) => vivo && setD(r)).catch(() => vivo && setD({ errore: true }));
    return () => { vivo = false; };
  }, [st.id, tick]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!d) return <div className="mt-2 rounded-xl bg-emerald-50/60 p-3 text-xs text-slate-400" data-testid="cliente-ev-pun-log-loading">Carico lo storico…</div>;
  if (d.errore) return null;
  return (
    <div className="mt-2 space-y-2 rounded-xl border border-emerald-200 bg-emerald-50/50 p-3" data-testid="cliente-ev-pun-log">
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-bold uppercase tracking-wide text-slate-500">Cosa sta facendo Vesta</span>
        <span data-testid="cliente-ev-pun-log-stato"
          className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold ${d.sotto_media ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>
          {d.sotto_media ? "Ore convenienti" : "In attesa"}
        </span>
      </div>
      {d.azione_corrente && <p className="text-xs font-semibold text-slate-700" data-testid="cliente-ev-pun-log-azione">Adesso: {d.azione_corrente}</p>}
      <AvvisoPun />
      <div className="max-h-44 space-y-1.5 overflow-y-auto" data-testid="cliente-ev-pun-log-storico">
        {d.storico.length === 0 ? (
          <p className="py-2 text-center text-xs text-slate-400">Ancora nessuna azione registrata. Appena il prezzo PUN varia, qui vedrai cosa decide Vesta.</p>
        ) : d.storico.map((r, i) => {
          const ic = LOG_ICON[r.icona] || LOG_ICON.info;
          return (
            <div key={i} className="flex items-start gap-2 rounded-lg bg-white p-2 ring-1 ring-emerald-100" data-testid="cliente-ev-pun-log-riga">
              <ic.I className={`mt-0.5 h-4 w-4 shrink-0 ${ic.c}`} />
              <div className="min-w-0">
                <div className="text-xs leading-snug text-slate-700">{r.testo}</div>
                <div className="text-[10px] text-slate-400">{fdt(r.at)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const GIORNI_LABEL = [["Lun", 0], ["Mar", 1], ["Mer", 2], ["Gio", 3], ["Ven", 4], ["Sab", 5], ["Dom", 6]];
const riassuntoGiorni = (g) => {
  if (!g || g.length === 0) return "tutti i giorni";
  const s = [...g].sort((a, b) => a - b).join(",");
  if (s === "0,1,2,3,4") return "giorni feriali";
  if (s === "5,6") return "weekend";
  return [...g].sort((a, b) => a - b).map((i) => GIORNI_LABEL[i][0]).join(" ");
};

// Avviso: il PUN all'ingrosso non è il prezzo finale in bolletta.
const AvvisoPun = () => (
  <p className="flex items-start gap-1.5 rounded-lg bg-amber-50 px-2 py-1.5 text-[10.5px] leading-snug text-amber-700 ring-1 ring-amber-100" data-testid="cliente-ev-prog-pun-avviso">
    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
    <span>Il <b>PUN</b> è il prezzo all'ingrosso dell'energia, <b>non</b> il prezzo finale in bolletta (esclude spread del fornitore, oneri, imposte e IVA). Serve solo a scegliere le ore più convenienti.</span>
  </p>
);

// Pannello PUN (solo Pro): prezzo attuale, media del giorno, serie prossime 24h con ore sotto la media
// evidenziate in verde (quelle in cui Vesta carica in automatico) e riga di stato.
function PunPanel({ src, sid, inCarica }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    let vivo = true;
    src.evPun(sid).then((r) => vivo && setD(r)).catch(() => vivo && setD({ disponibile: false }));
    return () => { vivo = false; };
  }, [sid]); // eslint-disable-line react-hooks/exhaustive-deps
  const eur = (v) => (v == null ? "—" : `${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 3, maximumFractionDigits: 3 })} €/kWh`);
  if (!d) return <p className="text-[11px] text-slate-400" data-testid="cliente-ev-pun-loading">Carico i prezzi PUN…</p>;
  if (!d.disponibile) return <p className="text-[11px] text-slate-400" data-testid="cliente-ev-pun-nodata">Prezzi PUN non ancora disponibili.</p>;
  const serie = (d.serie || []).map((p) => ({ ...p, val: p.prezzo != null ? +Number(p.prezzo).toFixed(4) : null }));
  const stato = inCarica && d.sotto_media
    ? { t: "Sta caricando: prezzo sotto la media", c: "text-emerald-600", dot: "bg-emerald-500" }
    : d.sotto_media
      ? { t: "Prezzo sotto la media: ore convenienti per caricare", c: "text-emerald-600", dot: "bg-emerald-500" }
      : { t: "In attesa di ore più convenienti", c: "text-slate-500", dot: "bg-slate-300" };
  return (
    <div className="space-y-2" data-testid="cliente-ev-pun-panel">
      <div className="grid grid-cols-2 gap-2">
        <div className="rounded-lg bg-white p-2 ring-1 ring-slate-200">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">PUN ora</div>
          <div className="text-base font-extrabold text-slate-800" data-testid="cliente-ev-pun-ora">{eur(d.prezzo_ora)}</div>
        </div>
        <div className="rounded-lg bg-white p-2 ring-1 ring-slate-200">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Media di oggi</div>
          <div className="text-base font-extrabold text-slate-800" data-testid="cliente-ev-pun-media">{eur(d.media_oggi)}</div>
        </div>
      </div>
      <div className={`flex items-center gap-1.5 text-[11px] font-semibold ${stato.c}`} data-testid="cliente-ev-pun-stato">
        <span className={`h-2 w-2 rounded-full ${stato.dot}`} />{stato.t}
      </div>
      <div>
        <div className="mb-1 flex items-center justify-between text-[10px] font-semibold">
          <span className="uppercase tracking-wide text-slate-400">Prossime 24 ore · €/kWh</span>
          <span className="flex items-center gap-1 text-emerald-600"><span className="h-2 w-2 rounded-sm bg-emerald-500" />carica (sotto la media)</span>
        </div>
        <div className="h-32 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={serie} margin={{ top: 4, right: 6, left: 4, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
              <XAxis dataKey="t" tick={{ fontSize: 9 }} minTickGap={24} />
              <YAxis tick={{ fontSize: 9 }} width={42} tickFormatter={(v) => Number(v).toFixed(2)} />
              <Tooltip formatter={(v) => [eur(v), "PUN"]} labelFormatter={(l) => `Ore ${l}`} labelStyle={{ color: "#0f172a", fontWeight: 700 }} itemStyle={{ color: "#0f172a" }} />
              {d.media_oggi != null && <ReferenceLine y={d.media_oggi} stroke="#94a3b8" strokeDasharray="4 3" />}
              <Bar dataKey="val" radius={[3, 3, 0, 0]}>
                {serie.map((p, i) => <Cell key={i} fill={p.economica ? "#10b981" : "#cbd5e1"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

// Ricarica programmata avanzata: giorni della settimana + più fasce orarie (ognuna "col sole"),
// oppure modalità "pronta entro un orario" (Vesta insegue il sole e garantisce la carica prima del deadline).
// In entrambe si può attivare la "Carica a PUN" (prezzi all'ingrosso dell'energia).
function Programma({ src, st, prog, run, busy, pro, inCarica }) {
  const [tipo, setTipo] = useState("fasce");
  const [giorni, setGiorni] = useState([]);
  const [fasce, setFasce] = useState([{ inizio: "10:00", fine: "16:00", sole: false }]);
  const [orario, setOrario] = useState("07:00");
  const [kwh, setKwh] = useState(10);
  const [prontaSole, setProntaSole] = useState(true);
  const [minorCosto, setMinorCosto] = useState(false);
  const [punOn, setPunOn] = useState(false);
  const [aperto, setAperto] = useState(false);
  const attivo = !!prog?.attivo;

  useEffect(() => {
    if (!prog) return;
    setTipo(prog.tipo || "fasce");
    setGiorni(prog.giorni || []);
    if (prog.fasce?.length) setFasce(prog.fasce.map((f) => ({ inizio: f.inizio, fine: f.fine, sole: !!f.sole })));
    if (prog.pronta) { setOrario(prog.pronta.orario || "07:00"); setKwh(prog.pronta.kwh || 10); setProntaSole(!!prog.pronta.sole); setMinorCosto(!!prog.pronta.minor_costo); }
    setPunOn(!!prog.pun_auto);
    setAperto(!!prog.attivo);
  }, [st.id, prog]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGiorno = (i) => setGiorni((g) => (g.includes(i) ? g.filter((x) => x !== i) : [...g, i]));
  const setFascia = (i, patch) => setFasce((fs) => fs.map((f, k) => (k === i ? { ...f, ...patch } : f)));
  const addFascia = () => setFasce((fs) => (fs.length >= 3 ? fs : [...fs, { inizio: "22:00", fine: "06:00", sole: false }]));
  const removeFascia = (i) => setFasce((fs) => fs.filter((_, k) => k !== i));

  const body = () => tipo === "pronta"
    ? { attivo: true, tipo: "pronta", pronta: { orario, kwh: Number(kwh), sole: prontaSole, minor_costo: minorCosto } }
    : { attivo: true, tipo: "fasce", giorni, fasce, pun_auto: punOn };
  const salva = () => run(() => src.evSchedule(st.id, body()), "Programma salvato");
  const toggleAttivo = () => {
    if (aperto) {
      if (attivo) run(() => src.evSchedule(st.id, { attivo: false }), "Programma disattivato");
      setAperto(false);
    } else {
      setAperto(true);
    }
  };

  const tabCls = (on) => `flex-1 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors ${on ? "bg-white text-sky-700 shadow-sm" : "text-slate-500"}`;

  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2.5" data-testid="cliente-ev-prog">
      <div className="flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Clock className="h-4 w-4 text-sky-600" />Ricarica programmata</span>
        <Toggle on={aperto} disabled={busy} testid="cliente-ev-prog-toggle" onChange={toggleAttivo} />
      </div>

      {aperto && (<>
      {attivo && prog?.riassunto && <p className="mt-1 text-[11px] font-semibold text-sky-700" data-testid="cliente-ev-prog-riassunto">{prog.riassunto}</p>}

      <div className="mt-2 flex gap-1 rounded-xl bg-slate-200/60 p-1">
        <button type="button" className={tabCls(tipo === "fasce")} onClick={() => setTipo("fasce")} data-testid="cliente-ev-prog-tab-fasce">Fasce orarie</button>
        <button type="button" className={tabCls(tipo === "pronta")} onClick={() => setTipo("pronta")} data-testid="cliente-ev-prog-tab-pronta">Pronta entro…</button>
      </div>

      {tipo === "fasce" ? (
        <div className="mt-3 space-y-3">
          <div>
            <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Giorni · {riassuntoGiorni(giorni)}</div>
            <div className="flex gap-1">
              {GIORNI_LABEL.map(([lab, i]) => (
                <button key={i} type="button" onClick={() => toggleGiorno(i)} data-testid={`cliente-ev-prog-giorno-${i}`}
                  className={`h-8 flex-1 rounded-lg text-[11px] font-bold transition-colors ${giorni.includes(i) ? "bg-sky-600 text-white" : "bg-white text-slate-500 ring-1 ring-slate-200"}`}>{lab}</button>
              ))}
            </div>
            <div className="mt-1.5 flex gap-2">
              <button type="button" onClick={() => setGiorni([])} className="text-[10px] font-bold uppercase tracking-wide text-sky-600" data-testid="cliente-ev-prog-giorni-tutti">Tutti</button>
              <button type="button" onClick={() => setGiorni([0, 1, 2, 3, 4])} className="text-[10px] font-bold uppercase tracking-wide text-sky-600" data-testid="cliente-ev-prog-giorni-feriali">Feriali</button>
              <button type="button" onClick={() => setGiorni([5, 6])} className="text-[10px] font-bold uppercase tracking-wide text-sky-600" data-testid="cliente-ev-prog-giorni-weekend">Weekend</button>
            </div>
          </div>

          <div className="space-y-2">
            {fasce.map((f, i) => (
              <div key={i} className="rounded-lg bg-white p-2 ring-1 ring-slate-200" data-testid={`cliente-ev-prog-fascia-${i}`}>
                <div className="flex items-center gap-2">
                  <input type="time" value={f.inizio} onChange={(e) => setFascia(i, { inizio: e.target.value })} className="h-9 flex-1 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-800 [color-scheme:light]" data-testid={`cliente-ev-prog-fascia-${i}-inizio`} />
                  <span className="text-slate-400">→</span>
                  <input type="time" value={f.fine} onChange={(e) => setFascia(i, { fine: e.target.value })} className="h-9 flex-1 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-800 [color-scheme:light]" data-testid={`cliente-ev-prog-fascia-${i}-fine`} />
                  {fasce.length > 1 && (
                    <button type="button" onClick={() => removeFascia(i)} className="text-slate-300 transition-colors hover:text-rose-500" data-testid={`cliente-ev-prog-fascia-${i}-remove`}><X className="h-4 w-4" /></button>
                  )}
                </div>
                <label className="mt-1.5 flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600"><Sun className="h-3.5 w-3.5 text-amber-500" />Carica dinamica Ai VESTA in questa fascia</span>
                  <Toggle on={f.sole} disabled={busy} testid={`cliente-ev-prog-fascia-${i}-sole`} onChange={() => setFascia(i, { sole: !f.sole })} />
                </label>
              </div>
            ))}
            {fasce.length < 3 && (
              <button type="button" onClick={addFascia} className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 py-2 text-xs font-bold text-slate-500 transition-colors hover:border-sky-400 hover:text-sky-600" data-testid="cliente-ev-prog-add-fascia"><Plus className="h-3.5 w-3.5" />Aggiungi fascia</button>
            )}
          </div>
          <p className="text-[11px] text-slate-400">La ricarica si avvia e si ferma da sola negli orari scelti. Con "Carica dinamica Ai VESTA" attiva, nella fascia la potenza insegue il surplus fotovoltaico.</p>

          {pro && (
            <div className="rounded-lg bg-white p-2 ring-1 ring-slate-200">
              <label className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600"><TrendingDown className="h-3.5 w-3.5 text-emerald-600" />Carica a PUN · automatico (sotto la media del giorno)</span>
                <Toggle on={punOn} disabled={busy} testid="cliente-ev-prog-pun-toggle" onChange={() => setPunOn((v) => !v)} />
              </label>
              {punOn && (
                <div className="mt-2 space-y-2">
                  <p className="text-[11px] text-slate-400">Nella fascia, Vesta carica dalla rete <b>automaticamente</b> solo nelle ore in cui il PUN è sotto la media del giorno. Nelle ore più care sfrutta il surplus fotovoltaico, se "Carica dinamica" è attiva.</p>
                  <PunPanel src={src} sid={st.id} inCarica={inCarica} />
                  <AvvisoPun />
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {pro && (
            <label className="flex items-center justify-between rounded-lg bg-white p-2 ring-1 ring-slate-200">
              <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600"><TrendingDown className="h-3.5 w-3.5 text-emerald-600" />Carica a PUN · alle ore più economiche</span>
              <Toggle on={minorCosto} disabled={busy} testid="cliente-ev-prog-pronta-minorcosto" onChange={() => setMinorCosto((v) => !v)} />
            </label>
          )}
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Pronta entro le</div>
              <input type="time" value={orario} onChange={(e) => setOrario(e.target.value)} className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-800 [color-scheme:light]" data-testid="cliente-ev-prog-pronta-orario" />
            </div>
            {!minorCosto && (
              <div className="flex-1">
                <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">Energia da caricare</div>
                <div className="flex items-center gap-1">
                  <input type="number" min="1" max="100" step="1" value={kwh} onChange={(e) => setKwh(e.target.value)} className="h-9 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-800" data-testid="cliente-ev-prog-pronta-kwh" />
                  <span className="text-sm font-bold text-slate-500">kWh</span>
                </div>
              </div>
            )}
          </div>
          <label className="flex items-center justify-between rounded-lg bg-white p-2 ring-1 ring-slate-200">
            <span className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600"><Sun className="h-3.5 w-3.5 text-amber-500" />{minorCosto ? "Nelle ore care usa il sole gratis" : "Priorità al sole prima del boost finale"}</span>
            <Toggle on={prontaSole} disabled={busy} testid="cliente-ev-prog-pronta-sole" onChange={() => setProntaSole((v) => !v)} />
          </label>
          {minorCosto ? (
            <>
              <p className="text-[11px] text-slate-400">Vesta carica nelle ore con il PUN più basso fino all'orario scelto. In Versione Pro, con "usa il sole gratis", nelle ore più care sfrutta prima il surplus fotovoltaico.</p>
              <AvvisoPun />
            </>
          ) : (
            <p className="text-[11px] text-slate-400">Vesta usa il sole quando c'è e garantisce la carica richiesta completandola a piena potenza nelle ore prima dell'orario scelto.</p>
          )}
        </div>
      )}

      <button type="button" disabled={busy} onClick={salva} data-testid="cliente-ev-prog-save"
        className="mt-3 h-9 w-full rounded-lg bg-sky-600 text-sm font-bold text-white transition-colors hover:bg-sky-700 disabled:opacity-60">Salva programma</button>
      </>)}
    </div>
  );
}

export function Vettura({ src, st }) {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [apri, setApri] = useState(false);
  const [cerca, setCerca] = useState(false);
  const [q, setQ] = useState("");
  const [ris, setRis] = useState(null);
  const [cercando, setCercando] = useState(false);
  const [manuale, setManuale] = useState(false);
  const [mForm, setMForm] = useState({ marca: "", modello: "", battery_kwh: "", consumo_kwh_100: "" });
  const [mErr, setMErr] = useState(null);
  const etich = (v) => `${v.nome}${v.variante ? ` · ${v.variante}` : ""}${v.anno ? ` ${v.anno}` : ""}`;

  const load = () => src.evVettura(st.id).then(setD).catch(() => setD({ errore: true }));
  useEffect(() => { load(); }, [st.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const salva = async (body) => { setBusy(true); try { setD(await src.evVetturaSet(st.id, body)); } catch { /* ignora */ } finally { setBusy(false); } };
  const doCerca = async () => { setCercando(true); try { const r = await src.evCatalog(q); setRis(r.risultati || []); } catch { setRis([]); } finally { setCercando(false); } };
  const salvaManuale = async () => {
    setMErr(null);
    const bk = parseFloat(String(mForm.battery_kwh).replace(",", "."));
    if (!mForm.marca.trim() && !mForm.modello.trim()) { setMErr("Indica marca o modello"); return; }
    if (!bk || bk <= 0) { setMErr("Inserisci la capacità della batteria (kWh)"); return; }
    setBusy(true);
    try {
      const r = await src.evVetturaSet(st.id, { azione: "salva_manuale", marca: mForm.marca, modello: mForm.modello, battery_kwh: bk, consumo_kwh_100: mForm.consumo_kwh_100 ? String(mForm.consumo_kwh_100).replace(",", ".") : null });
      setD(r); setManuale(false); setCerca(false); setMForm({ marca: "", modello: "", battery_kwh: "", consumo_kwh_100: "" });
    } catch (e) { setMErr(e?.response?.data?.detail || "Errore nel salvataggio"); } finally { setBusy(false); }
  };

  if (!d || d.errore) return null;
  const sel = d.auto_selezionata;
  const stima = d.stima_ricarica;
  const pct = d.percentuale_attuale;

  return (
    <div className="rounded-xl bg-slate-50 px-3 py-2.5" data-testid="cliente-ev-vettura">
      <button type="button" onClick={() => setApri((v) => !v)} data-testid="cliente-ev-vettura-toggle"
        className="flex w-full items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700"><Car className="h-4 w-4 text-sky-600" />La tua auto</span>
        <span className="flex items-center gap-1 text-xs font-bold text-slate-500">
          {sel ? <span className="max-w-[150px] truncate" data-testid="cliente-ev-vettura-nome" title={etich(sel)}>{etich(sel)}</span> : <span className="text-sky-600">Scegli</span>}
          <ChevronDown className={`h-4 w-4 transition-transform ${apri ? "rotate-180" : ""}`} />
        </span>
      </button>

      {apri && (
        <div className="mt-3 space-y-3">
          {cerca ? (
            <div className="space-y-2" data-testid="cliente-ev-vettura-ricerca">
              <div className="flex gap-2">
                <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doCerca()} placeholder="Cerca marca o modello…"
                  className="h-9 flex-1 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-800" data-testid="cliente-ev-vettura-cerca-input" />
                <button type="button" onClick={doCerca} disabled={cercando} className="flex h-9 items-center justify-center rounded-lg bg-sky-600 px-3 text-white disabled:opacity-60" data-testid="cliente-ev-vettura-cerca-btn"><Search className="h-4 w-4" /></button>
              </div>
              <div className="max-h-48 space-y-1 overflow-y-auto">
                {cercando ? <p className="text-xs text-slate-400">Cerco…</p> : (ris || []).map((v) => (
                  <button key={v.id} type="button" disabled={busy}
                    onClick={() => salva({ azione: "salva", auto_id: v.id }).then(() => { setCerca(false); setRis(null); setQ(""); })}
                    className="flex w-full items-center justify-between gap-2 rounded-lg bg-white p-2 text-left ring-1 ring-slate-100 hover:bg-sky-50" data-testid={`cliente-ev-vettura-ris-${v.id}`}>
                    <span className="min-w-0"><span className="block truncate text-sm font-bold text-slate-800">{v.nome}</span><span className="text-[11px] text-slate-400">{[v.variante, `${v.battery_kwh} kWh`, v.range_km ? `${v.range_km} km` : null].filter(Boolean).join(" · ")}</span></span>
                    <Plus className="h-4 w-4 shrink-0 text-sky-600" />
                  </button>
                ))}
                {ris && ris.length === 0 && !cercando && <p className="text-xs text-slate-400">Nessun risultato.</p>}
              </div>
              <div className="flex items-center justify-between">
                <button type="button" onClick={() => { setCerca(false); setRis(null); setManuale(false); setMErr(null); }} className="text-[11px] font-bold text-slate-400" data-testid="cliente-ev-vettura-annulla">Annulla</button>
                {!manuale && <button type="button" onClick={() => setManuale(true)} className="text-[11px] font-bold text-sky-600" data-testid="cliente-ev-vettura-manuale-apri">Non trovi la tua auto? Aggiungila a mano</button>}
              </div>
              {manuale && (
                <div className="space-y-2 rounded-lg bg-white p-2.5 ring-1 ring-slate-200" data-testid="cliente-ev-vettura-manuale">
                  <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500">Aggiungi auto manualmente</div>
                  <div className="grid grid-cols-2 gap-2">
                    <input value={mForm.marca} onChange={(e) => { setMErr(null); setMForm((f) => ({ ...f, marca: e.target.value })); }} placeholder="Marca" className="h-9 rounded-lg border border-slate-200 px-2 text-sm text-slate-800" data-testid="cliente-ev-vettura-manuale-marca" />
                    <input value={mForm.modello} onChange={(e) => { setMErr(null); setMForm((f) => ({ ...f, modello: e.target.value })); }} placeholder="Modello" className="h-9 rounded-lg border border-slate-200 px-2 text-sm text-slate-800" data-testid="cliente-ev-vettura-manuale-modello" />
                    <label className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-semibold text-slate-400">Capacità pacco (kWh)</span>
                      <input type="number" min="1" max="300" step="0.1" value={mForm.battery_kwh} onChange={(e) => { setMErr(null); setMForm((f) => ({ ...f, battery_kwh: e.target.value })); }} placeholder="es. 60" className="h-9 rounded-lg border border-slate-200 px-2 text-sm text-slate-800" data-testid="cliente-ev-vettura-manuale-kwh" />
                    </label>
                    <label className="flex flex-col gap-0.5">
                      <span className="text-[10px] font-semibold text-slate-400">Consumo medio (kWh/100km)</span>
                      <input type="number" min="1" max="60" step="0.1" value={mForm.consumo_kwh_100} onChange={(e) => { setMErr(null); setMForm((f) => ({ ...f, consumo_kwh_100: e.target.value })); }} placeholder="facolt." className="h-9 rounded-lg border border-slate-200 px-2 text-sm text-slate-800" data-testid="cliente-ev-vettura-manuale-consumo" />
                    </label>
                  </div>
                  {mErr && <p className="text-[11px] font-semibold text-rose-500" data-testid="cliente-ev-vettura-manuale-err">{mErr}</p>}
                  <p className="text-[10px] text-slate-400">Il consumo medio è solo informativo: i tempi di ricarica si basano sulla capacità del pacco (kWh).</p>
                  <div className="flex gap-2">
                    <button type="button" disabled={busy} onClick={salvaManuale} className="h-9 flex-1 rounded-lg bg-sky-600 text-sm font-bold text-white disabled:opacity-60" data-testid="cliente-ev-vettura-manuale-salva">Salva auto</button>
                    <button type="button" onClick={() => { setManuale(false); setMErr(null); }} className="h-9 rounded-lg px-3 text-sm font-bold text-slate-400" data-testid="cliente-ev-vettura-manuale-chiudi">Chiudi</button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <>
              {(d.auto_salvate || []).length > 0 ? (
                <div className="flex flex-wrap gap-1.5" data-testid="cliente-ev-vettura-salvate">
                  {d.auto_salvate.map((v) => {
                    const on = sel && v.id === sel.id;
                    return (
                      <span key={v.id} title={etich(v)} className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${on ? "bg-sky-600 text-white" : "bg-white text-slate-600 ring-1 ring-slate-200"}`}>
                        <button type="button" disabled={busy} onClick={() => salva({ azione: "scegli", auto_id: v.id })} data-testid={`cliente-ev-vettura-pick-${v.id}`} className="max-w-[170px] truncate">{etich(v)}</button>
                        <button type="button" disabled={busy} onClick={() => salva({ azione: "rimuovi", auto_id: v.id })} data-testid={`cliente-ev-vettura-del-${v.id}`} className={on ? "text-white/80" : "text-slate-300"}><X className="h-3 w-3" /></button>
                      </span>
                    );
                  })}
                  <button type="button" onClick={() => setCerca(true)} className="rounded-full bg-white px-2.5 py-1 text-xs font-bold text-sky-600 ring-1 ring-sky-200" data-testid="cliente-ev-vettura-aggiungi"><Plus className="inline h-3 w-3" /> Aggiungi</button>
                </div>
              ) : (
                <button type="button" onClick={() => setCerca(true)} className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-sky-300 py-2 text-xs font-bold text-sky-600" data-testid="cliente-ev-vettura-scegli">
                  <Search className="h-3.5 w-3.5" />Scegli la tua auto
                </button>
              )}

              {sel && (
                <>
                  <div className="rounded-lg bg-white p-2.5 ring-1 ring-slate-200">
                    <div className="mb-1 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wide text-slate-500"><BatteryCharging className="h-4 w-4 text-emerald-600" />Batteria adesso</span>
                      <span className="text-sm font-extrabold text-emerald-600" data-testid="cliente-ev-vettura-pct-val">{pct == null ? "—" : `${pct}%`}</span>
                    </div>
                    <input type="range" min={0} max={100} step={5} value={pct ?? 50} disabled={busy}
                      onChange={(e) => setD({ ...d, percentuale_attuale: Number(e.target.value) })}
                      onPointerUp={(e) => salva({ azione: "stato", start_pct: Number(e.target.value) })}
                      onTouchEnd={(e) => salva({ azione: "stato", start_pct: Number(e.target.value) })}
                      onKeyUp={(e) => { if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End", "PageUp", "PageDown"].includes(e.key)) salva({ azione: "stato", start_pct: Number(e.target.value) }); }}
                      className="w-full accent-emerald-600" data-testid="cliente-ev-vettura-pct" />
                    <div className="mt-1 flex items-center justify-between">
                      <span className="text-[11px] text-slate-400">Carica fino a</span>
                      <div className="flex gap-1" data-testid="cliente-ev-vettura-target">
                        {[80, 90, 100].map((t) => (
                          <button key={t} type="button" disabled={busy} onClick={() => salva({ azione: "stato", target_pct: t })}
                            className={`rounded-md px-2 py-0.5 text-[11px] font-bold ${d.percentuale_obiettivo === t ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-500"}`} data-testid={`cliente-ev-vettura-target-${t}`}>{t}%</button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {stima ? (
                    <div className="rounded-lg bg-sky-50 p-2.5 ring-1 ring-sky-100" data-testid="cliente-ev-vettura-stima">
                      <div className="grid grid-cols-2 gap-2 text-center">
                        <div><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Da caricare</div><div className="text-sm font-extrabold text-slate-800" data-testid="cliente-ev-vettura-kwh">{kwh(stima.kwh_da_caricare)}</div></div>
                        <div><div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Tempo stimato</div><div className="text-sm font-extrabold text-slate-800" data-testid="cliente-ev-vettura-tempo">{stima.durata || "—"}</div></div>
                      </div>
                      {stima.costo_adesso_eur != null && (
                        <div className="mt-2 border-t border-sky-100 pt-2 text-[12px] text-slate-600">
                          <div className="flex justify-between"><span>Caricando adesso</span><b data-testid="cliente-ev-vettura-costo-ora">{eur(stima.costo_adesso_eur)}</b></div>
                          <div className="flex justify-between"><span>Alle ore più convenienti (PUN)</span><b className="text-emerald-600" data-testid="cliente-ev-vettura-costo-pun">{eur(stima.costo_a_pun_eur)}</b></div>
                          {stima.risparmio_eur > 0 && (
                            <div className="mt-1 flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-1 text-[11px] font-bold text-emerald-700" data-testid="cliente-ev-vettura-risparmio">
                              <TrendingDown className="h-3.5 w-3.5 shrink-0" />Risparmi ~{eur(stima.risparmio_eur)}{stima.risparmio_pct ? ` (−${stima.risparmio_pct}%)` : ""} caricando a PUN{stima.inizio_consigliato_pun ? `, verso le ${stima.inizio_consigliato_pun}` : ""}
                            </div>
                          )}
                        </div>
                      )}
                      <p className="mt-1 text-[10px] text-slate-400">Stima indicativa: sopra l'80% l'auto rallenta e il freddo allunga i tempi. Il PUN non è il prezzo finale in bolletta.</p>
                      {d.consiglio && (
                        <div className="mt-2 flex items-start gap-1.5 rounded-lg bg-sky-600/5 px-2 py-1.5 text-[11px] font-semibold leading-snug text-sky-800 ring-1 ring-sky-100" data-testid="cliente-ev-vettura-consiglio">
                          <Car className="mt-0.5 h-3.5 w-3.5 shrink-0 text-sky-600" />
                          <span>{d.consiglio}</span>
                        </div>
                      )}
                    </div>
                  ) : (pct != null && d.percentuale_obiettivo != null && pct >= d.percentuale_obiettivo) ? (
                    <p className="flex items-center gap-1.5 rounded-lg bg-emerald-50 px-2 py-1.5 text-[11px] font-semibold text-emerald-700" data-testid="cliente-ev-vettura-gia-carica"><BatteryCharging className="h-3.5 w-3.5 shrink-0" />La batteria è già al livello di ricarica desiderato ({d.percentuale_obiettivo}%).</p>
                  ) : (
                    <p className="text-[11px] text-slate-400" data-testid="cliente-ev-vettura-no-stima">Imposta la percentuale attuale della batteria per vedere la stima di tempi e costi.</p>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Comandi({ src, st }) {
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [speed, setSpeed] = useState(null);
  const [logTick, setLogTick] = useState(0);
  const [aperto, setAperto] = useState(false);

  const load = () => src.evControls(st.id).then((d) => {
    setC(d);
    setSpeed(d.limit_w ?? d.max_w ?? null);
  }).catch(() => setC({ errore: true }));
  useEffect(() => { load(); }, [st.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const flash = (t) => { setMsg(t); setTimeout(() => setMsg(null), 2500); };
  const run = async (fn, okMsg) => {
    setBusy(true);
    try { await fn(); flash(okMsg); await load(); setLogTick((t) => t + 1); }
    catch (e) { flash(e?.response?.data?.detail || "Operazione non riuscita, riprova"); }
    finally { setBusy(false); }
  };

  if (!c) return <div className="rounded-2xl bg-slate-50 p-3 text-sm text-slate-400" data-testid="cliente-ev-comandi-loading">Carico i comandi…</div>;
  if (c.errore) return null;

  return (
    <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100" data-testid="cliente-ev-comandi">
      <button type="button" disabled={busy}
        onClick={() => run(() => (c.in_carica ? src.evStop(st.id) : src.evStart(st.id)), c.in_carica ? "Ricarica fermata" : "Ricarica avviata")}
        data-testid="cliente-ev-cmd-toggle"
        className={`mb-3 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-extrabold text-white transition-colors disabled:opacity-60 ${c.in_carica ? "bg-rose-500 hover:bg-rose-600" : "bg-emerald-600 hover:bg-emerald-700"}`}>
        {c.in_carica ? <><Square className="h-4 w-4" />Ferma ricarica</> : <><Play className="h-4 w-4" />Avvia ricarica</>}
      </button>

      {c.tetto_contatore?.sta_limitando_ora && (
        <div className="mb-3 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-[12px] leading-snug text-amber-800 ring-1 ring-amber-200" data-testid="cliente-ev-tetto-avviso">
          <Gauge className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <span><b>Ricarica limitata a {c.tetto_contatore.limite_kw} kW dal contatore.</b> {c.tetto_contatore.motivo}</span>
        </div>
      )}

      <button type="button" onClick={() => setAperto((v) => !v)} data-testid="cliente-ev-comandi-toggle"
        className="flex w-full items-center justify-between text-xs font-bold uppercase tracking-wide text-slate-400">
        <span>Gestisci la ricarica</span>
        <ChevronDown className={`h-4 w-4 transition-transform ${aperto ? "rotate-180" : ""}`} />
      </button>

      {!aperto ? null : (
      <div className="mt-3 space-y-3">
      {c.pro && <Vettura src={src} st={st} />}

      <div className={`rounded-xl bg-slate-50 px-3 py-2.5 ${c.solare ? "opacity-70" : ""}`}>
        <div className="mb-1 flex items-center justify-between">
          <span className="text-sm font-semibold text-slate-700">Velocità di ricarica</span>
          <span className="text-sm font-extrabold text-sky-600" data-testid="cliente-ev-cmd-speed-value">{c.solare ? kwTxt(c.auto_limit_w ?? c.max_w) : kwTxt(speed)}</span>
        </div>
        <input type="range" min={c.min_w || 1380} max={c.max_w || 7360} step={100} value={c.solare ? (c.auto_limit_w ?? c.max_w ?? 7360) : (speed ?? c.max_w ?? 7360)}
          onChange={(e) => setSpeed(Number(e.target.value))}
          onPointerUp={(e) => run(() => src.evSpeed(st.id, Number(e.target.value)), "Velocità aggiornata")}
          onTouchEnd={(e) => run(() => src.evSpeed(st.id, Number(e.target.value)), "Velocità aggiornata")}
          disabled={busy || c.solare} className="w-full accent-sky-600 disabled:cursor-not-allowed" data-testid="cliente-ev-cmd-speed" />
        <div className="flex justify-between text-[10px] font-semibold text-slate-400"><span>{kwTxt(c.min_w)}</span><span>{kwTxt(c.max_w)}</span></div>
        {c.tetto_contatore?.limita && (
          <p className="mt-1 flex items-center gap-1 text-[11px] text-slate-500" data-testid="cliente-ev-tetto-limite">
            <Gauge className="h-3.5 w-3.5 shrink-0 text-slate-400" />Per non far scattare il contatore, in automatico la ricarica non supera <b>{c.tetto_contatore.limite_kw} kW</b> · {c.tetto_contatore.modalita === "dinamica" ? "in base ai consumi di casa" : "limite prudenziale"}
          </p>
        )}
        {c.solare && <p className="mt-1 text-[11px] text-slate-400" data-testid="cliente-ev-cmd-speed-note">Carica dinamica attiva: la potenza la decide il sole (allineata al valore autosettato) e non è modificabile a mano.</p>}
      </div>

      <div className="flex items-center justify-between rounded-xl bg-slate-950 px-3 py-3 ring-1 ring-slate-800 shadow-sm">
        <span className="flex items-center gap-3 text-sm font-semibold text-white">
          <span className="relative inline-flex h-11 w-[132px] shrink-0 items-center justify-center overflow-hidden rounded-md">
            <video autoPlay muted loop playsInline preload="auto" className="h-full w-full object-cover" data-testid="cliente-ev-vesta-logo">
              <source src="/ai-vesta-logo.webm" type="video/webm" />
              <source src="/ai-vesta-logo.mp4" type="video/mp4" />
            </video>
          </span>
          Carica dinamica
        </span>
        <Toggle on={c.solare} disabled={busy} testid="cliente-ev-cmd-solar"
          onChange={() => run(() => src.evSolar(st.id, !c.solare), !c.solare ? "Carica dinamica Ai VESTA attivata" : "Carica dinamica Ai VESTA disattivata")} />
      </div>

      {c.solare && <DinamicaLog src={src} st={st} tick={logTick} />}

      {c.pro && (
        <div className="rounded-xl bg-slate-50 px-3 py-2.5" data-testid="cliente-ev-pun-ottimizza">
          <div className="flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-700">
              <TrendingDown className="h-4 w-4 text-emerald-600" />Ottimizza a PUN
            </span>
            <Toggle on={!!c.pun_base} disabled={busy} testid="cliente-ev-pun-ottimizza-toggle"
              onChange={() => run(() => src.evPunOttimizza(st.id, !c.pun_base), !c.pun_base ? "Ottimizzazione a PUN attivata" : "Ottimizzazione a PUN disattivata")} />
          </div>
          <p className="mt-1 text-[11px] text-slate-400">Vesta carica dalla rete solo nelle ore in cui il prezzo PUN è sotto la media del giorno. Cede la precedenza alla ricarica programmata e alla carica dinamica.</p>
          <div className="mt-2"><PunPanel src={src} sid={st.id} inCarica={c.in_carica} /></div>
          {c.pun_base && <PunLog src={src} st={st} tick={logTick} />}
        </div>
      )}

      <Programma src={src} st={st} prog={c.programma} run={run} busy={busy} pro={c.pro} inCarica={c.in_carica} />

      {msg && <div className="text-center text-xs font-semibold text-slate-500" data-testid="cliente-ev-cmd-msg">{msg}</div>}
      </div>
      )}
    </div>
  );
}

// Pannello informativo "i · funzioni": spiega al cliente come si combinano le funzioni di ricarica
// in base al tetto del contatore e alla produzione fotovoltaica.
function FunzioniInfo({ onClose }) {
  const H = ({ children }) => <h4 className="mt-3 flex items-center gap-1.5 text-sm font-extrabold text-slate-800">{children}</h4>;
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/50 sm:items-center sm:p-4" onClick={onClose} data-testid="cliente-ev-funzioni-info-dialog">
      <div className="cliente-font flex max-h-[88dvh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white sm:max-h-[85vh] sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex shrink-0 items-center justify-between border-b border-slate-100 p-4">
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900"><Info className="h-5 w-5 text-sky-600" />Come funziona la ricarica</h2>
          <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 transition-colors hover:text-slate-700" aria-label="Chiudi" data-testid="cliente-ev-funzioni-info-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain p-4 text-sm leading-relaxed text-slate-600" data-testid="cliente-ev-funzioni-info-body">
          <p>La ricarica si regola in automatico combinando le funzioni attive con il <b>tetto del contatore</b> e la <b>produzione del tuo impianto fotovoltaico</b>.</p>

          <div className="rounded-2xl bg-slate-50 p-3">
            <H><Gauge className="h-4 w-4 text-sky-600" />Il tetto del contatore</H>
            <p>È il limite massimo di prelievo dalla rete, per <b>non far scattare il contatore</b> (potenza contrattuale + una piccola tolleranza).</p>
            <ul className="mt-1 list-disc space-y-1 pl-4">
              <li><b>Modalità prudenziale</b>: tiene da parte una quota fissa per i consumi di casa.</li>
              <li><b>Modalità in tempo reale</b>: sottrae i consumi di casa nell'istante; se la casa consuma molto, all'auto resta meno potenza.</li>
            </ul>
            <p className="mt-1 text-[12px] text-slate-500">Il tetto limita solo la ricarica <b>dalla rete</b>: quando carichi <b>col sole</b> non interviene.</p>
          </div>

          <div className="rounded-2xl bg-amber-50/70 p-3 ring-1 ring-amber-100">
            <H><Sun className="h-4 w-4 text-amber-500" />Carica dinamica (col sole)</H>
            <p>La potenza segue il <b>sole che avanza</b> (produzione − consumi di casa). Tanto sole → carica forte; poco sole → rallenta; niente sole → si ferma. Ha priorità sul PUN e di solito non è vincolata dal tetto.</p>
          </div>

          <div className="rounded-2xl bg-emerald-50/70 p-3 ring-1 ring-emerald-100">
            <H><TrendingDown className="h-4 w-4 text-emerald-600" />Ottimizza a PUN (da rete)</H>
            <p>Carica <b>dalla rete solo quando il prezzo è sotto la media</b> del giorno; sopra la media va in pausa. Quando carica, la potenza è <b>limitata dal tetto del contatore</b>.</p>
          </div>

          <div className="rounded-2xl bg-sky-50/70 p-3 ring-1 ring-sky-100">
            <H><Clock className="h-4 w-4 text-sky-600" />Ricarica programmata</H>
            <p>Comanda lei dentro le fasce orarie impostate (e può usare il prezzo PUN o garantire l'auto pronta a un'ora). Anche qui, se carica da rete, la potenza rispetta il tetto del contatore.</p>
          </div>

          <div className="rounded-2xl bg-slate-50 p-3">
            <H>Più funzioni insieme</H>
            <p>Le priorità sono: <b>Programmata → Carica dinamica (sole) → Ottimizza a PUN</b>.</p>
            <p className="mt-1">Se sono attive insieme <b>sole</b> e <b>PUN</b>, decide la <b>copertura del fotovoltaico</b>: con buona copertura si insegue il sole, con poca copertura si carica da rete nelle ore convenienti, sempre entro il tetto del contatore.</p>
          </div>

          <p className="text-[12px] text-slate-400">In sintesi: c'è sole → si usa prima il sole; poco sole e si carica da rete → la potenza viene limitata al tetto del contatore.</p>
        </div>
      </div>
    </div>
  );
}

export function EvDialog({ src, data, onClose }) {
  const stazioni = data.stazioni || [];
  const eur = (v) => (v == null ? "—" : "€ " + Number(v).toFixed(2).replace(".", ","));
  const [si, setSi] = useState(0);
  const st = stazioni[si] || {};
  const sessioni = st.sessioni_logiche || st.sessioni || [];
  const [selSid, setSelSid] = useState(sessioni[0]?.id || null);
  const [meter, setMeter] = useState(null);
  const [caricando, setCaricando] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);

  // al cambio colonnina seleziona la sua ultima ricarica
  useEffect(() => {
    setSelSid(sessioni[0]?.id || null);
  }, [si]); // eslint-disable-line react-hooks/exhaustive-deps

  const selSess = sessioni.find((s) => s.id === selSid);
  useEffect(() => {
    if (!selSess) { setMeter({ points: [] }); return; }
    setCaricando(true);
    const ids = (selSess.parti_ids || [selSess.id]).join(",");
    src.evMeterMulti(ids).then(setMeter).catch(() => setMeter({ points: [] })).finally(() => setCaricando(false));
  }, [selSid]); // eslint-disable-line react-hooks/exhaustive-deps

  const points = (meter?.points || []).map((p) => ({ t: hhmm(p.ts), kw: p.power_w != null ? +(p.power_w / 1000).toFixed(2) : null }));
  const nPause = selSess?.interruzioni || 0;
  const titoloGrafico = selSess ? (selSess.in_corso ? "Ricarica in corso" : "Ricarica") : "Potenza di ricarica";

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 sm:items-center sm:p-4" onClick={onClose} data-testid="cliente-ev-dialog">
      <div className="cliente-font w-full max-w-lg overflow-hidden rounded-t-3xl bg-white sm:rounded-3xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 p-4">
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-slate-900"><PlugZap className="h-5 w-5 text-sky-600" />Ricarica auto</h2>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setInfoOpen(true)} data-testid="cliente-ev-funzioni-info"
              className="flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700 ring-1 ring-sky-200 transition-colors hover:bg-sky-100">
              <Info className="h-3.5 w-3.5" />funzioni
            </button>
            <button onClick={onClose} className="rounded-full p-1.5 text-slate-400 transition-colors hover:text-slate-700" data-testid="cliente-ev-close"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="max-h-[75vh] space-y-4 overflow-y-auto p-4">
          {stazioni.length > 1 && (
            <div className="flex flex-wrap gap-2" data-testid="cliente-ev-stations-switch">
              {stazioni.map((s, i) => (
                <button key={s.id} type="button" onClick={() => setSi(i)} data-testid={`cliente-ev-station-pick-${s.id}`}
                  className={`rounded-full px-3 py-1.5 text-sm font-bold transition-colors ${i === si ? "bg-sky-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{s.nome}</button>
              ))}
            </div>
          )}

          <div className="space-y-3 rounded-2xl bg-slate-50 p-3" data-testid={`cliente-ev-station-${st.id}`}>
            <div className="flex items-center justify-between">
              <span className="truncate font-bold text-slate-800">{st.nome}</span>
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${st.in_carica ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}`}>{st.in_carica ? "In ricarica" : "Non in ricarica"}</span>
            </div>
            {st.da_sole && (
              <div className="flex items-center gap-2 rounded-xl bg-amber-50 p-2.5 ring-1 ring-amber-100" data-testid="cliente-ev-sole">
                <Sun className="h-5 w-5 shrink-0 text-amber-500" />
                <span className="text-sm font-semibold text-slate-700">Stai caricando col sole · <b className="text-amber-600">{st.solar_pct}%</b> dal fotovoltaico</span>
              </div>
            )}
            <div className="grid grid-cols-3 gap-2 text-center">
              <Idx label="Potenza" value={pw(st.potenza_w)} />
              <Idx label="Da sole" value={st.solar_pct == null ? "—" : `${st.solar_pct}%`} accent={COL.sole} />
              <Idx label="Totale caricato" value={kwh(st.energia_totale_kwh)} />
            </div>
            {st.diesel?.configurata && st.diesel?.mese?.risparmio_eur > 0 && (
              <div className="flex items-start gap-2 rounded-xl bg-emerald-50 p-2.5 ring-1 ring-emerald-100" data-testid="cliente-ev-diesel-mese">
                <Fuel className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                <div className="text-sm leading-snug text-slate-700">
                  <span className="font-semibold">A {st.diesel.mese.mese_label} hai risparmiato </span>
                  <b className="text-emerald-600" data-testid="cliente-ev-diesel-mese-val">{eur(st.diesel.mese.risparmio_eur)}</b>
                  <span className="font-semibold"> rispetto a un'auto diesel</span>
                  <div className="mt-0.5 text-[11px] text-slate-500">Elettrico {eur(st.diesel.mese.costo_pun_eur)} · gasolio equivalente {eur(st.diesel.mese.costo_diesel_eur)}. {st.diesel.origine_gasolio === "riserva" ? "Stima su un prezzo del gasolio indicativo (media nazionale di riferimento)." : "Stima sul prezzo medio nazionale del gasolio."}</div>
                </div>
              </div>
            )}
            {st.diesel?.pro && !st.diesel?.configurata && (
              <div className="flex items-start gap-2 rounded-xl bg-sky-50 p-2.5 ring-1 ring-sky-100" data-testid="cliente-ev-diesel-config">
                <Fuel className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
                <span className="text-sm leading-snug text-slate-700">Vuoi vedere quanto risparmi rispetto a un'auto diesel? Configura la tua auto in <b>Gestisci la ricarica → La tua auto</b>.</span>
              </div>
            )}
          </div>

          <Comandi src={src} st={st} />

          <div data-testid="cliente-ev-chart">
            <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">{titoloGrafico} · potenza (kW){nPause > 0 ? ` · ${nPause} ${nPause === 1 ? "pausa" : "pause"}` : ""}</div>
            {caricando ? (
              <p className="py-6 text-center text-sm text-slate-400">Carico il grafico…</p>
            ) : points.length > 0 ? (
              <div className="h-48 w-full">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={points} margin={{ top: 5, right: 8, left: -12, bottom: 0 }}>
                    <defs><linearGradient id="evgrad" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor={COL.sole} stopOpacity={0.5} /><stop offset="100%" stopColor={COL.sole} stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                    <XAxis dataKey="t" tick={{ fontSize: 10 }} minTickGap={28} />
                    <YAxis tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="kw" stroke={COL.sole} strokeWidth={2} fill="url(#evgrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <p className="py-6 text-center text-sm text-slate-400">Nessun dato di potenza per questa sessione.</p>
            )}
            {selSess && selSess.costo_pun != null && (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm" data-testid="ev-costo-riepilogo">
                <span className="font-semibold text-slate-700">Costo ricarica (PUN): <b className="text-slate-900">{eur(selSess.costo_pun)}</b></span>
                {selSess.risparmio_pun > 0 && (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-bold text-emerald-700" data-testid="ev-costo-risparmio">
                    Risparmiati {eur(selSess.risparmio_pun)} rispetto alla tariffa media
                  </span>
                )}
                {selSess.costo_parziale && <span className="text-xs text-slate-400">stima parziale (prezzi PUN non completi)</span>}
              </div>
            )}
          </div>

          {sessioni.length > 0 && (
            <div data-testid="cliente-ev-sessioni">
              <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-400">Sessioni di ricarica</div>
              <div className="space-y-1.5">
                {sessioni.map((s) => {
                  const sel = s.id === selSid;
                  return (
                    <button key={s.id} type="button" onClick={() => setSelSid(s.id)} data-testid={`cliente-ev-session-${s.id}`}
                      className={`flex w-full items-center justify-between gap-3 rounded-xl p-2.5 text-left transition-colors ${sel ? "bg-sky-50 ring-1 ring-sky-300" : "bg-white ring-1 ring-slate-100 hover:bg-slate-50"}`}>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-bold text-slate-800">
                          {fdt(s.inizio)} {s.in_corso ? <span className="text-emerald-600">· in corso</span> : s.fine ? `→ ${hhmm(s.fine)}` : ""}
                        </div>
                        <div className="text-xs text-slate-400">{[durata(s.durata_s), s.interruzioni > 0 ? `${s.interruzioni} ${s.interruzioni === 1 ? "pausa" : "pause"} (PUN/programma)` : (s.in_corso ? "in ricarica" : "completata")].filter(Boolean).join(" · ")}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {s.risparmio_pun > 0 && (
                            <div className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700" data-testid={`ev-risparmio-${s.id}`}>
                              <Sun className="h-3 w-3" />−{eur(s.risparmio_pun)} grazie al PUN
                            </div>
                          )}
                          {st.diesel?.configurata && s.risparmio_diesel > 0 && (
                            <div className="inline-flex items-center gap-1 rounded-full bg-emerald-600/10 px-1.5 py-0.5 text-[11px] font-bold text-emerald-700" data-testid={`ev-diesel-${s.id}`}>
                              <Fuel className="h-3 w-3" />+{eur(s.risparmio_diesel)} vs diesel
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="shrink-0 text-right">
                        <div className="text-sm font-extrabold text-slate-800">{kwh(s.energia_kwh)}</div>
                        {s.costo_pun != null && <div className="text-xs font-semibold text-slate-500" data-testid={`ev-costo-${s.id}`}>{eur(s.costo_pun)}</div>}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <p className="text-xs text-slate-400">La quota dal sole è una stima basata sulla produzione dell'impianto; diventerà precisa quando saranno disponibili i dati di consumo.</p>
        </div>
      </div>
      {infoOpen && <FunzioniInfo onClose={() => setInfoOpen(false)} />}
    </div>
  );
}


